'use strict';

// Optional biometric unlock (Face ID / Touch ID / Windows Hello) via WebAuthn + the PRF
// extension. The PRF output is a stable per-credential secret released ONLY after user
// verification (the biometric), so we use it to AES-GCM-wrap the user's password. The
// wrapped password is useless without the authenticator — so this does NOT put a reversible
// password on the device (unlike a naive "unlock gate"); it preserves the zero-knowledge
// model: the password is still the root, just unlocked by your face/finger. If the platform
// has no authenticator or no PRF, we feature-detect and fall back to the password.

const { subtle, randomBytes } = require('./crypto/lib');
const { toB64, fromB64 } = require('./crypto/b64');

const PRF_INFO = 'web3keys/webauthn-prf/v1:';
const enc = (s) => new TextEncoder().encode(s);
async function sha256(bytes) { return new Uint8Array(await subtle().digest('SHA-256', bytes)); }
const prfSalt = (email) => sha256(enc(PRF_INFO + String(email).toLowerCase()));

// Is a platform authenticator (built-in biometric) present?
async function available() {
  try {
    if (typeof window === 'undefined' || !window.PublicKeyCredential || !navigator.credentials) return false;
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (_) { return false; }
}

async function importPrfKey(prfBytes) {
  return subtle().importKey('raw', prfBytes, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}
async function wrapSecret(prfBytes, plaintext) {
  const iv = randomBytes(12);
  const ct = await subtle().encrypt({ name: 'AES-GCM', iv }, await importPrfKey(prfBytes), enc(plaintext));
  return { iv: toB64(iv), ct: toB64(new Uint8Array(ct)) };
}
async function unwrapSecret(prfBytes, blob) {
  const pt = await subtle().decrypt({ name: 'AES-GCM', iv: fromB64(blob.iv) }, await importPrfKey(prfBytes), fromB64(blob.ct));
  return new TextDecoder().decode(pt);
}

// Run an assertion against a stored credential and return its PRF secret (or null).
async function getPrfSecret(email, credentialId) {
  const salt = await prfSalt(email);
  const assertion = await navigator.credentials.get({
    publicKey: {
      challenge: randomBytes(32),
      allowCredentials: [{ type: 'public-key', id: fromB64(credentialId) }],
      userVerification: 'required',
      extensions: { prf: { eval: { first: salt } } },
      timeout: 60000,
    },
  });
  const res = assertion && assertion.getClientExtensionResults && assertion.getClientExtensionResults();
  const first = res && res.prf && res.prf.results && res.prf.results.first;
  return first ? new Uint8Array(first) : null;
}

/**
 * Enroll biometric unlock for `email`, wrapping `password` under the credential's PRF secret.
 * Returns { credentialId, wrapped } to persist on this device. Throws if PRF is unsupported.
 */
async function enroll(email, password) {
  const userId = await sha256(enc('web3keys/user:' + String(email).toLowerCase()));
  const cred = await navigator.credentials.create({
    publicKey: {
      rp: { id: location.hostname, name: 'Web3Keys' },
      user: { id: userId, name: email, displayName: email },
      challenge: randomBytes(32),
      pubKeyCredParams: [{ type: 'public-key', alg: -7 }, { type: 'public-key', alg: -257 }],
      authenticatorSelection: { authenticatorAttachment: 'platform', userVerification: 'required', residentKey: 'preferred' },
      extensions: { prf: {} },
      timeout: 60000,
    },
  });
  if (!cred) throw new Error('enrollment cancelled');
  const credentialId = toB64(new Uint8Array(cred.rawId));
  const prf = await getPrfSecret(email, credentialId);
  if (!prf) throw new Error('This device doesn’t support biometric unlock (no PRF).');
  return { credentialId, wrapped: await wrapSecret(prf, password) };
}

/** Unlock: assert with the stored credential, return the unwrapped password. */
async function unlock(email, enrollment) {
  const prf = await getPrfSecret(email, enrollment.credentialId);
  if (!prf) throw new Error('biometric unlock unavailable');
  return unwrapSecret(prf, enrollment.wrapped);
}

module.exports = { available, enroll, unlock };
